import { createElement, Fragment, useMemo, type ReactNode } from 'react';

import { cn } from '@/lib/utils';

/**
 * Render seguro de HTML generado en el panel anterior (respuestas del FAQ).
 *
 * El HTML lo administran usuarios del sistema, así que **no** se inyecta con
 * `dangerouslySetInnerHTML`: se parsea con `DOMParser` y se reconstruye un árbol
 * de React usando sólo etiquetas de una lista blanca. Cualquier etiqueta no
 * permitida se descarta conservando su texto, y los enlaces se limitan a
 * `http(s)`, `mailto` y rutas relativas.
 */

const ALLOWED_TAGS = new Set([
  'P', 'BR', 'HR', 'STRONG', 'B', 'EM', 'I', 'U', 'S',
  'UL', 'OL', 'LI', 'A', 'CODE', 'PRE', 'BLOCKQUOTE',
  'H1', 'H2', 'H3', 'H4', 'H5', 'DIV', 'SPAN', 'SMALL',
  'TABLE', 'THEAD', 'TBODY', 'TR', 'TH', 'TD',
]);

const CLASSES: Record<string, string> = {
  P: 'text-ink-700',
  UL: 'my-2 list-disc space-y-1 pl-5',
  OL: 'my-2 list-decimal space-y-1 pl-5',
  LI: 'text-ink-700',
  STRONG: 'font-semibold text-ink-900',
  B: 'font-semibold text-ink-900',
  CODE: 'rounded bg-ink-100 px-1 py-0.5 font-mono text-[0.85em] text-ink-800',
  PRE: 'my-2 overflow-x-auto rounded-lg bg-ink-950 p-3 font-mono text-xs text-white',
  BLOCKQUOTE: 'my-2 border-l-2 border-brand-200 pl-3 text-ink-600 italic',
  H1: 'mt-3 text-base font-semibold text-ink-900',
  H2: 'mt-3 text-base font-semibold text-ink-900',
  H3: 'mt-2 text-sm font-semibold text-ink-900',
  H4: 'mt-2 text-sm font-semibold text-ink-900',
  TABLE: 'my-2 w-full border-collapse text-xs',
  TH: 'border-b border-ink-200 px-2 py-1 text-left font-semibold text-ink-700',
  TD: 'border-b border-ink-100 px-2 py-1 text-ink-600',
  HR: 'my-3 border-ink-100',
};

function isSafeHref(href: string): boolean {
  const value = href.trim();

  if (value === '') return false;
  if (value.startsWith('/') || value.startsWith('#')) return true;

  return /^(https?:\/\/|mailto:|tel:)/i.test(value);
}

function convert(node: Node, key: string, linkClass: string): ReactNode {
  if (node.nodeType === Node.TEXT_NODE) {
    return node.textContent;
  }

  if (node.nodeType !== Node.ELEMENT_NODE) return null;

  const element = node as HTMLElement;
  const tag = element.tagName.toUpperCase();

  // Se conserva el contenido aunque la etiqueta no esté permitida.
  if (!ALLOWED_TAGS.has(tag)) {
    return (
      <Fragment key={key}>
        {Array.from(element.childNodes).map((child, index) => convert(child, `${key}-${index}`, linkClass))}
      </Fragment>
    );
  }

  if (tag === 'BR') return <br key={key} />;

  const children = Array.from(element.childNodes).map((child, index) =>
    convert(child, `${key}-${index}`, linkClass),
  );

  if (tag === 'A') {
    const href = element.getAttribute('href') ?? '';
    if (!isSafeHref(href)) return <Fragment key={key}>{children}</Fragment>;

    return (
      <a
        key={key}
        href={href}
        target="_blank"
        rel="noreferrer noopener"
        className={linkClass}
      >
        {children}
      </a>
    );
  }

  const className = CLASSES[tag];

  return createElement(tag.toLowerCase(), { key, className }, children);
}

interface HtmlContentProps {
  html: string | null | undefined;
  className?: string;
  /** Clase de los enlaces internos del contenido. */
  linkClassName?: string;
}

export function HtmlContent({ html, className, linkClassName }: HtmlContentProps) {
  const content = useMemo(() => {
    if (!html || typeof window === 'undefined') return null;

    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const linkClass = linkClassName ?? 'font-medium text-brand-600 underline hover:text-brand-700';

    return Array.from(parsed.body.childNodes).map((node, index) => convert(node, `n-${index}`, linkClass));
  }, [html, linkClassName]);

  if (!html) return null;

  return <div className={cn('space-y-1 text-sm leading-relaxed', className)}>{content}</div>;
}
